/**
 * ProtectedRoute sends a signed-out visitor from e.g. /post/<id> to /auth, and the path is lost. This remembers it (this tab only,
 * 30 minutes) so the "Open in app" button on /auth can still hand the app the post the visitor was trying to see.
 * Same idea and same safety rules as market-return.ts: only a same-site path is ever stored or returned.
 * No imports from "@/..." on purpose (the test runs with plain node and passes its own fake storage).
 */
import { isSafeAppPath } from "./app-handoff.ts";

const KEY = "sksnap.handoffPath";
const TTL_MS = 30 * 60 * 1000;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const defaultStore = (): Store | null => {
  try { return typeof sessionStorage === "undefined" ? null : sessionStorage; } catch { return null; }
};

export function rememberHandoffPath(path: string, now: number = Date.now(), store: Store | null = defaultStore()): void {
  if (!store || !isSafeAppPath(path)) return;
  try { store.setItem(KEY, JSON.stringify({ path, at: now })); } catch { /* storage blocked: the app just opens its home */ }
}

export function readHandoffPath(now: number = Date.now(), store: Store | null = defaultStore()): string | null {
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof v.path !== "string" || typeof v.at !== "number") return null;
    if (now - v.at > TTL_MS || v.at > now + 60_000) return null;
    return isSafeAppPath(v.path) ? v.path : null;
  } catch { return null; }
}
